import React from 'react';
import { Button } from 'baseui/button';
import { StatefulPopover, PLACEMENT } from 'baseui/popover';
import { useStyletron } from 'baseui';
import { CustomTheme } from '../baseweb-custom-theme/customTheme';
import { Block } from 'baseui/block';
import { Check, ChevronRight } from 'baseui/icon';
import {
  SortingDateCreatedProps,
  SortingLastModifiedProps,
  SortingNameProps,
} from '../relay-messenger-list/RelayMessenger.list';

export enum LastModifiedOldestNewestMethods {
  Oldest = 'Oldest',
  Newest = 'Newest',
  LastModified = 'Last modified',
  DateClosed = 'Date Closed',
}
interface SortingActionItemProps {
  dataTestId?: string;
  sortingName: SortingNameProps;
  sortDateCreated: SortingDateCreatedProps;
  sortLastModified: SortingLastModifiedProps;
  close: () => void;
  clearFilterState: () => void;
  clearSortingSelection: () => void;
  isclosedConversation?: boolean;
}

export const SortLastModifiedAction = ({
  dataTestId,
  sortingName,
  sortDateCreated,
  sortLastModified,
  close,
  clearFilterState,
  clearSortingSelection,
  isclosedConversation,
}: SortingActionItemProps) => {
  const [, theme] = useStyletron();
  const customTheme = theme as CustomTheme;

  return (
    <StatefulPopover
      content={() => (
        <Block
          overrides={{
            Block: {
              style: {
                width: '150px',
                display: 'flex',
                flexDirection: 'column',
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '10px',
                paddingRight: '10px',
              },
            },
          }}
        >
          <Button
            onClick={(e) => {
              sortLastModified.setSortByNewestLastModified(
                (e.target as HTMLButtonElement).value,
              );
              sortLastModified.setSortByOldestLastModified(null);
              sortingName.setSortByAsc(null);
              sortingName.setSortByDesc(null);
              sortDateCreated.setSortByOldestDateCreated(null);
              sortDateCreated.setSortByNewestDateCreated(null);
              close();
            }}
            value={LastModifiedOldestNewestMethods.Newest}
            overrides={{
              BaseButton: {
                style: {
                  padding: '10px',
                  width: '100%',
                  display: 'flex',
                  color:
                    sortLastModified.sortingOptionLastModifiedClicked &&
                    sortLastModified.sortByNewestLastModified
                      ? '#3693BF'
                      : 'black',
                  fontWeight:
                    sortLastModified.sortingOptionLastModifiedClicked &&
                    sortLastModified.sortByNewestLastModified
                      ? 'bolder'
                      : 'inherit',
                  backgroundColor: 'transparent',
                  textAlign: 'center',
                  justifyContent: 'left',
                  fontSize: '12px',
                  outline: 'none !important',
                  ':hover': {
                    backgroundColor: '#E1EFF5',
                  },
                  ':active': {
                    backgroundColor: 'transparent',
                  },
                },
                props: {
                  'data-testid': `${dataTestId}-newest`,
                },
              },
            }}
            startEnhancer={() =>
              sortLastModified.sortingOptionLastModifiedClicked &&
              sortLastModified.sortByNewestLastModified ? (
                <Check
                  size={'20px'}
                  color="#3693BF"
                  overrides={{
                    Svg: {
                      style: {
                        display: 'flex',
                      },
                    },
                  }}
                />
              ) : (
                ''
              )
            }
          >
            {LastModifiedOldestNewestMethods.Newest}
          </Button>
          <Button
            onClick={(e) => {
              sortLastModified.setSortByOldestLastModified(
                (e.target as HTMLButtonElement).value,
              );
              sortLastModified.setSortByNewestLastModified(null);
              sortingName.setSortByAsc(null);
              sortingName.setSortByDesc(null);
              sortDateCreated.setSortByOldestDateCreated(null);
              sortDateCreated.setSortByNewestDateCreated(null);
              close();
            }}
            value={LastModifiedOldestNewestMethods.Oldest}
            overrides={{
              BaseButton: {
                style: {
                  padding: '10px',
                  width: '100%',
                  display: 'flex',
                  color:
                    sortLastModified.sortingOptionLastModifiedClicked &&
                    sortLastModified.sortByOldestLastModified
                      ? '#3693BF'
                      : 'black',
                  fontWeight:
                    sortLastModified.sortingOptionLastModifiedClicked &&
                    sortLastModified.sortByOldestLastModified
                      ? 'bolder'
                      : 'inherit',
                  backgroundColor: 'transparent',
                  textAlign: 'center',
                  justifyContent: 'left',
                  fontSize: '12px',
                  outline: 'none !important',
                  ':hover': {
                    backgroundColor: '#E1EFF5',
                  },
                  ':active': {
                    backgroundColor: 'transparent',
                  },
                },
                props: {
                  'data-testid': `${dataTestId}-oldest`,
                },
              },
            }}
            startEnhancer={() =>
              sortLastModified.sortingOptionLastModifiedClicked &&
              sortLastModified.sortByOldestLastModified ? (
                <Check
                  size={'20px'}
                  color="#3693BF"
                  overrides={{
                    Svg: {
                      style: {
                        display: 'flex',
                      },
                    },
                  }}
                />
              ) : (
                ''
              )
            }
          >
            {LastModifiedOldestNewestMethods.Oldest}
          </Button>
        </Block>
      )}
      placement={PLACEMENT.right}
      autoFocus={false}
      overrides={{
        Inner: {
          style: {
            backgroundColor: customTheme.colors.white,
            borderBottomLeftRadius: '5px',
            borderBottomRightRadius: '5px',
            borderTopLeftRadius: '5px',
            borderTopRightRadius: '5px',
          },
          props: { 'data-testid': 'sorting-last-modified-popover' },
        },
        Body: {
          style: {
            borderBottomLeftRadius: '5px',
            borderBottomRightRadius: '5px',
            borderTopLeftRadius: '5px',
            borderTopRightRadius: '5px',
            marginTop: '0',
            marginLeft: '25px',
          },
        },
      }}
    >
      <Block
        overrides={{
          Block: {
            style: {
              width: '190px',
            },
          },
        }}
      >
        <Button
          onClick={() => {
            sortLastModified.setSortOptionLastModifiedClicked(
              LastModifiedOldestNewestMethods.LastModified,
            );
            sortingName.setSortOptionNameClicked(null);
            sortDateCreated.setSortOptionDateCreatedClicked(null);
            clearFilterState();
            clearSortingSelection();
          }}
          value={LastModifiedOldestNewestMethods.LastModified}
          overrides={{
            BaseButton: {
              style: {
                width: '100%',
                display: 'flex',
                color: sortLastModified.sortingOptionLastModifiedClicked
                  ? '#3693BF'
                  : 'black',
                backgroundColor:
                  sortLastModified.sortingOptionLastModifiedClicked
                    ? '#E1EFF5'
                    : 'transparent',
                fontWeight: sortLastModified.sortingOptionLastModifiedClicked
                  ? 'bolder'
                  : 'inherit',
                textAlign: 'left',
                fontSize: '12px',
                outline: 'none !important',
                justifyContent: 'left',
                ':hover': {
                  backgroundColor: 'none',
                },
                ':active': {
                  backgroundColor: 'none',
                },
              },
              props: {
                'data-testid': 'last-modified-btn',
              },
            },
          }}
          endEnhancer={() => <ChevronRight />}
          startEnhancer={() =>
            sortLastModified.sortingOptionLastModifiedClicked &&
            (sortLastModified.sortByNewestLastModified ||
              sortLastModified.sortByOldestLastModified) ? (
              <Check size={'30px'} color="#3693BF" />
            ) : (
              ''
            )
          }
        >
          {isclosedConversation
            ? LastModifiedOldestNewestMethods.DateClosed
            : LastModifiedOldestNewestMethods.LastModified}
        </Button>
      </Block>
    </StatefulPopover>
  );
};
